import { Icons } from '../Icons';

interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg';
  message?: string;
  fullscreen?: boolean;
}

/**
 * LoadingSpinner Component
 *
 * Displays an animated loading spinner
 * Can be fullscreen or inline
 */
export function LoadingSpinner({ size = 'md', message, fullscreen = false }: LoadingSpinnerProps) {
  const sizes = {
    sm: 20,
    md: 32,
    lg: 48
  };

  const spinner = (
    <div
      role="status"
      aria-busy="true"
      aria-label={message || 'Chargement en cours'}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '16px',
        ...(fullscreen ? {
          position: 'fixed',
          inset: 0,
          background: 'var(--card)',
          zIndex: 9999
        } : {
          padding: '40px',
          minHeight: '200px'
        })
      }}
    >
      <div style={{
        animation: 'spin 1s linear infinite'
      }} aria-hidden="true">
        <Icons.Loader size={sizes[size]} style={{ color: 'var(--accent)' }} />
      </div>

      {message && (
        <p style={{
          fontSize: '14px',
          color: 'var(--muted)',
          fontWeight: 500
        }}>
          {message}
        </p>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );

  return spinner;
}

/**
 * LoadingOverlay Component
 *
 * Semi-transparent overlay with spinner
 * Used to indicate loading state over existing content
 */
export function LoadingOverlay({ message }: { message?: string }) {
  return (
    <div
      role="status"
      aria-busy="true"
      aria-label={message || 'Chargement en cours'}
      style={{
        position: 'absolute',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '8px',
        zIndex: 10
      }}
    >
      <div style={{
        padding: '24px 32px',
        background: 'var(--card)',
        borderRadius: '12px',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '12px'
      }}>
        <div style={{ animation: 'spin 1s linear infinite' }} aria-hidden="true">
          <Icons.Loader size={32} style={{ color: 'var(--accent)' }} />
        </div>
        {message && (
          <p style={{ fontSize: '14px', color: 'var(--text)', margin: 0 }}>
            {message}
          </p>
        )}
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
